import { PROVIDER_PATHS, appPaths } from "@/constants/paths";
import { Privilege } from "@/context/constant";
import { ImportInitialDataTypeEnum } from "@/services";
import { SelectItem } from "@/utils/utils";
import { CatalogTab } from "./catalog-types";

/** Parent master-data module; a grant here covers every catalog tab. */
export const MASTER_DATA_PRIVILEGE: Privilege = "Master Data";

/**
 * Granular privilege that unlocks the row actions of each catalog tab.
 * HCPCS has no dedicated privilege, it rides along with the procedure codes.
 */
export const CATALOG_CODE_PRIVILEGE = {
  CPT: "Master Data > CPT Codes",
  HCPCS: "Master Data > CPT Codes",
  ICD: "Master Data > ICD Codes",
  DRUG_CATALOG: "Master Data > Drug Catalogue",
} satisfies Record<string, Privilege>;

export type CatalogCodeType = keyof typeof CATALOG_CODE_PRIVILEGE;

export const CATALOG_ACTION = {
  EDIT: "edit",
  ARCHIVE: "delete",
  UNARCHIVE: "unarchive",
} as const;

export const CATALOG_ARCHIVED_ACTIONS: SelectItem[] = [
  { value: CATALOG_ACTION.UNARCHIVE, label: "Unarchive" },
];

export const CATALOG_ACTIVE_ACTIONS: SelectItem[] = [
  { value: CATALOG_ACTION.EDIT, label: "Edit" },
  { value: CATALOG_ACTION.ARCHIVE, label: "Archive" },
];

export const CATALOG_TAB_VALUE = {
  LOINC: "loinc",
  DRUG: "drug",
  ICD: "icd",
  CPT: "cpt",
  HCPCS: "hcpcs",
} as const;

export const CATALOG_IMPORT_TYPE = {
  LOINC: ImportInitialDataTypeEnum.Loinc,
  DRUG_CATALOG: ImportInitialDataTypeEnum.DrugCatalog,
  ICD: ImportInitialDataTypeEnum.Icd,
  CPT: ImportInitialDataTypeEnum.Cpt,
  HCPCS: ImportInitialDataTypeEnum.Hcpcs,
} as const;

export const CATALOG_TABS: CatalogTab[] = [
  {
    value: CATALOG_TAB_VALUE.LOINC,
    importType: CATALOG_IMPORT_TYPE.LOINC,
    adminPath: appPaths.libraryCatalogLOINCCodePath,
    providerPath: PROVIDER_PATHS.libraryCatalogLOINCCodePath,
    label: "LOINC Code",
    addButtonLabel: "Add LOINC Code",
  },
  {
    value: CATALOG_TAB_VALUE.DRUG,
    importType: CATALOG_IMPORT_TYPE.DRUG_CATALOG,
    adminPath: appPaths.libraryCatalogDrugCatalogPath,
    providerPath: PROVIDER_PATHS.libraryCatalogDrugCatalogPath,
    label: "Drug",
    addButtonLabel: "Add Drug",
  },
  {
    value: CATALOG_TAB_VALUE.ICD,
    importType: CATALOG_IMPORT_TYPE.ICD,
    adminPath: appPaths.libraryCatalogICDTenCodePath,
    providerPath: PROVIDER_PATHS.libraryCatalogICDTenCodePath,
    label: "Diagnosis Code",
    addButtonLabel: "Add Diagnosis Code",
  },
  {
    value: CATALOG_TAB_VALUE.CPT,
    importType: CATALOG_IMPORT_TYPE.CPT,
    adminPath: appPaths.libraryCatalogCPTCodePath,
    providerPath: PROVIDER_PATHS.libraryCatalogCPTCodePath,
    label: "CPT Code",
    addButtonLabel: "Add CPT Code",
  },
  {
    value: CATALOG_TAB_VALUE.HCPCS,
    importType: CATALOG_IMPORT_TYPE.HCPCS,
    adminPath: appPaths.libraryCatalogHCPCSCodePath,
    providerPath: PROVIDER_PATHS.libraryCatalogHCPCSCodePath,
    label: "HCPCS Code",
    addButtonLabel: "Add HCPCS Code",
  },
];

export const CATALOG_IMPORT_QUERY_KEY = "catalog-diagnosis-code-import";

export const CATALOG_IMPORT_NOTICE_SUFFIX =
  "data update available — please import from Catalog";

export const CATALOG_IMPORT_TAB_HINT = "New codes available to import";

export const CATALOG_IMPORT_NOTICE_ACTION_LABEL = "Go to Catalog";

export const CATALOG_IMPORT_LABEL_SEPARATOR = ", ";

export const CATALOG_TAB_IMPORT_NOTICE_SUFFIX =
  "data update available — please import to get the latest codes";

export const CATALOG_IMPORT_STARTED_SUFFIX =
  "data import has started. It will continue in the background and may take a few minutes to complete.";

export const CATALOG_IMPORT_STARTED_DISPLAY_MS = 3200;
